/**
 * ClinicConfigProvider — Single Source of Truth on the frontend.
 * ================================================================
 *
 * Every module in the app (Calendar, Reception, Waiting Room, Billing,
 * Inventory, Treatment Kits, Doctors, Staff, Online Booking, Analytics,
 * WhatsApp, Subscription, Settings, Copilot) must gate plan / caps /
 * feature flags via `useClinicConfig()` — never by reading `clinic.plan`
 * directly, and never with a hardcoded string like `plan === "solo"`.
 *
 * Freshness contract
 * ------------------
 * 1. On mount: fetch `/api/clinic-config` immediately.
 * 2. Poll every 15 seconds so a Platform-Admin plan change lands within
 *    at most 15s across every open tab.
 * 3. On window focus / tab visibility change: refetch immediately — most
 *    users will alt-tab back after making a purchase or an admin change.
 * 4. Via `BroadcastChannel("viyora-config")`: whenever any tab in the same
 *    browser session refetches and detects a `version` bump, it broadcasts
 *    the new payload to every other tab so they all update in lock-step.
 * 5. `refresh()` is exposed so any mutation (Add chair, save working hours,
 *    change plan, complete onboarding) can force-refresh the config right
 *    after its POST/PATCH returns — no wait needed.
 */
import {
  useEffect, useMemo, useRef, useState, useCallback, createContext, useContext,
} from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const CHANNEL_NAME = "viyora-config";
const POLL_INTERVAL_MS = 15_000;

const empty = {
  loading: true,
  config: null,
  version: null,
  refresh: () => Promise.resolve(),
  // Derived helpers — all no-ops until config loads.
  hasFeature: () => false,
  tier: null,
  caps: { chairs: 0, doctors: 0, receptionists: 0 },
  limits: { ai_requests: 0, whatsapp_msgs: 0, storage_mb: 0 },
  usage: null,
  plan: null,
  status: null,
  active: false,
  workingHours: null,
  bookingConfig: null,
  timezone: "Asia/Kolkata",
  workflowMode: null,
  counts: { doctors: 0, chairs: 0, receptionists: 0 },
};

const ClinicConfigContext = createContext(empty);

export function ClinicConfigProvider({ children }) {
  const { user } = useAuth();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const channelRef = useRef(null);
  const lastVersionRef = useRef(null);

  // Set config only when version differs — prevents redundant re-renders.
  const applyConfig = useCallback((next, broadcast) => {
    if (!next) return;
    if (next.version && next.version === lastVersionRef.current) return;
    lastVersionRef.current = next.version || null;
    setConfig(next);
    if (broadcast && channelRef.current) {
      try { channelRef.current.postMessage({ type: "config", payload: next }); }
      catch { /* channel closed — ignore */ }
    }
  }, []);

  const fetchConfig = useCallback(async ({ silent = false, broadcast = true } = {}) => {
    if (!user) { setConfig(null); setLoading(false); return; }
    if (!silent) setLoading(true);
    try {
      const { data } = await api.get("/clinic-config");
      applyConfig(data, broadcast);
    } catch {
      // Keep whatever we had — better a slightly stale entitlement than a
      // blank-slate that hides features. Loading flag falls to false so UIs
      // stop showing "Loading…".
    } finally {
      setLoading(false);
    }
  }, [user, applyConfig]);

  // ---- Cross-tab sync via BroadcastChannel ----------------------------------
  useEffect(() => {
    if (typeof BroadcastChannel === "undefined") return;
    const ch = new BroadcastChannel(CHANNEL_NAME);
    channelRef.current = ch;
    ch.onmessage = (evt) => {
      if (evt.data?.type === "config" && evt.data.payload) {
        applyConfig(evt.data.payload, false);
        setLoading(false);
      }
      if (evt.data?.type === "invalidate") {
        // Another tab told us to refetch (e.g. right after Add Chair).
        fetchConfig({ silent: true, broadcast: true });
      }
    };
    return () => { try { ch.close(); } catch { /* noop */ } };
  }, [applyConfig, fetchConfig]);

  // ---- Initial fetch + 15s poll --------------------------------------------
  useEffect(() => {
    fetchConfig({ silent: false, broadcast: true });
    if (!user) return;
    const t = setInterval(() => fetchConfig({ silent: true, broadcast: true }), POLL_INTERVAL_MS);
    return () => clearInterval(t);
  }, [user?.id, fetchConfig]);

  // ---- Refresh on window focus / tab visibility ----------------------------
  useEffect(() => {
    if (!user) return;
    const onFocus = () => fetchConfig({ silent: true, broadcast: true });
    const onVisible = () => { if (document.visibilityState === "visible") onFocus(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [user?.id, fetchConfig]);

  // Public refresh helper — used by any mutation to skip the poll wait.
  const refresh = useCallback(() => fetchConfig({ silent: true, broadcast: true }), [fetchConfig]);

  // Broadcast an "invalidate" nudge so every tab re-fetches. Cheap way to
  // sync after mutations that happen inside this tab.
  const invalidateAllTabs = useCallback(() => {
    if (channelRef.current) {
      try { channelRef.current.postMessage({ type: "invalidate" }); }
      catch { /* noop */ }
    }
    return refresh();
  }, [refresh]);

  // ---- Memoised derived helpers --------------------------------------------
  const value = useMemo(() => ({
    loading,
    config,
    version:      config?.version || null,
    refresh,
    invalidateAllTabs,
    // Feature helpers
    hasFeature:   (name) => !!config?.feature_flags?.[name],
    // Entitlement views
    tier:         config?.tier || null,
    caps:         config?.caps || empty.caps,
    limits:       config?.limits || empty.limits,
    usage:        config?.usage || null,
    plan:         config?.plan || null,
    status:       config?.status || null,
    active:       !!config?.active,
    // Operational views
    workingHours:  config?.working_hours || null,
    bookingConfig: config?.booking_config || null,
    timezone:      config?.timezone || "Asia/Kolkata",
    workflowMode:  config?.workflow_mode || null,
    counts:        config?.counts || empty.counts,
  }), [loading, config, refresh, invalidateAllTabs]);

  return (
    <ClinicConfigContext.Provider value={value}>
      {children}
    </ClinicConfigContext.Provider>
  );
}

export const useClinicConfig = () => useContext(ClinicConfigContext);

// Convenience hook: `const canWa = useFeatureFlag("whatsapp")`.
export const useFeatureFlag = (name) => {
  const { hasFeature, loading } = useClinicConfig();
  return { enabled: hasFeature(name), loading };
};
